if (!customElements.get('pansaar-carousel')) {
  customElements.define('pansaar-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll('[data-slide]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pause = this.querySelector('[data-pause]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = false;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      const go = (delta) => {
        this.stop();
        if (this.classList.contains('ps-hero')) this.goto((this.index + delta + this.slides.length) % this.slides.length);
        else this.track.scrollBy({ left: delta * this.track.clientWidth * .85, behavior: this.reduced.matches ? 'instant' : 'smooth' });
      };
      this.prev?.addEventListener('click', () => go(-1), opts);
      this.next?.addEventListener('click', () => go(1), opts);
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.stop(); this.goto(i); }, opts));
      this.pause?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pause.setAttribute('aria-pressed', String(this.paused));
        this.paused ? this.stop() : this.start();
      }, opts);
      this.addEventListener('mouseenter', () => this.stop(), opts);
      this.addEventListener('mouseleave', () => this.start(), opts);
      this.addEventListener('focusin', () => this.stop(), opts);
      this.addEventListener('focusout', () => this.start(), opts);
      this.track.addEventListener('pointerdown', () => this.stop(), opts);
      this.track.addEventListener('scroll', () => this.update(), { passive: true, signal: this.abort.signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); go(event.key === 'ArrowRight' ? 1 : -1); }
      }, opts);
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), opts);
      this.addEventListener('play', (event) => {
        if (event.target.tagName === 'VIDEO') this.querySelectorAll('video').forEach((video) => { if (video !== event.target) video.pause(); });
      }, { capture: true, signal: this.abort.signal });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.update();
      this.start();
    }
    goto(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: this.reduced.matches ? 'instant' : 'smooth' });
    }
    update() {
      if (!this.slides.length) return;
      const left = this.track.getBoundingClientRect().left;
      this.index = this.slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[best].getBoundingClientRect().left - left) ? i : best, 0);
      this.dots.forEach((dot, i) => i === this.index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
      if (!this.classList.contains('ps-hero')) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
      }
      if (this.classList.contains('ps-hero')) this.slides.forEach((slide, i) => { slide.inert = i !== this.index; });
    }
    stop() { clearInterval(this.timer); }
    start() {
      this.stop();
      if (this.dataset.auto !== 'true' || this.paused || this.reduced.matches || document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;
      this.timer = setInterval(() => this.goto((this.index + 1) % this.slides.length), 6000);
    }
    disconnectedCallback() { this.stop(); this.abort?.abort(); this.resize?.disconnect(); this.ready = false; }
  });
}

if (!customElements.get('pansaar-category-slider')) {
  customElements.define('pansaar-category-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-category-track]');
      this.group = this.querySelector('[data-category-group]');
      if (!this.track || !this.group || this.group.children.length < 2) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.dragging = false;
      this.visible = true;
      this.until = 0;
      this.position = this.track.scrollLeft;
      this.clone = this.group.cloneNode(true);
      this.clone.removeAttribute('data-category-group');
      this.clone.setAttribute('aria-hidden', 'true');
      this.clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      this.clone.querySelectorAll('*').forEach(el => {
        [...el.attributes].filter(a => a.name.startsWith('data-shopify')).forEach(a => el.removeAttribute(a.name));
      });
      this.clone.querySelectorAll('a,button,[tabindex]').forEach(el => el.tabIndex = -1);
      this.track.append(this.clone);
      this.pauseButton = this.querySelector('[data-category-pause]');
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.textContent = this.paused ? '▶' : 'Ⅱ';
      }, opts);
      const manual = () => { this.until = performance.now() + 8000; this.moving = false; };
      const go = (direction) => {
        manual();
        if (direction < 0 && this.track.scrollLeft < 2) this.track.scrollLeft = this.span;
        this.track.scrollBy({ left: direction * this.track.clientWidth * .65, behavior: this.reduced.matches ? 'instant' : 'smooth' });
      };
      this.querySelector('[data-category-prev]')?.addEventListener('click', () => go(-1), opts);
      this.querySelector('[data-category-next]')?.addEventListener('click', () => go(1), opts);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.hovered = true; }, opts);
      this.addEventListener('pointerleave', () => { this.hovered = false; }, opts);
      this.addEventListener('focusin', () => { this.focused = true; }, opts);
      this.addEventListener('focusout', () => { this.focused = this.contains(document.activeElement); }, opts);
      this.track.addEventListener('pointerdown', () => { this.dragging = true; manual(); }, opts);
      document.addEventListener('pointerup', () => { this.dragging = false; }, opts);
      document.addEventListener('pointercancel', () => { this.dragging = false; }, opts);
      this.track.addEventListener('wheel', manual, { passive: true, signal: this.abort.signal });
      this.track.addEventListener('keydown', event => {
        manual();
        if (event.target !== this.track) return;
        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault(); go(event.key === 'ArrowRight' ? 1 : -1);
        }
        if (event.key === 'Home') { event.preventDefault(); this.track.scrollLeft = 0; }
        if (event.key === 'End') { event.preventDefault(); this.track.scrollLeft = Math.max(0, this.span - this.track.clientWidth); }
      }, opts);
      this.resize = new ResizeObserver(() => {
        this.span = this.clone.offsetLeft - this.group.offsetLeft;
        this.moving = false;
      });
      this.resize.observe(this.group);
      this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
      this.intersection.observe(this);
      const frame = now => {
        const elapsed = this.last ? Math.min(now - this.last, 60) : 0;
        this.last = now;
        const play = this.dataset.auto === 'true' && this.dataset.editor !== 'true' && !this.reduced.matches && !this.paused && !this.hovered && !this.focused && !this.dragging && !document.hidden && this.visible && now >= this.until && this.span > 0;
        if (play) {
          if (!this.moving) this.position = this.track.scrollLeft;
          this.position = (this.position + elapsed * (Number(this.dataset.speed) || 16) / 1000) % this.span;
          this.track.scrollLeft = this.position;
          this.moving = true;
        } else {
          this.moving = false;
        }
        this.frame = requestAnimationFrame(frame);
      };
      this.frame = requestAnimationFrame(frame);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.clone?.remove();
      this.last = 0;
    }
  });
}


if (!customElements.get('pansaar-category-swipe')) {
  customElements.define('pansaar-category-swipe', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      const track = this.querySelector('[data-category-track]');
      if (!track || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.abort = new AbortController();
      this.cancelled = false;
      const options = { signal: this.abort.signal, passive: true };
      const cancel = () => {
        this.cancelled = true;
        clearTimeout(this.delay);
        clearTimeout(this.returnTimer);
        this.observer?.disconnect();
      };
      ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach(type => {
        this.addEventListener(type, cancel, options);
      });
      const start = () => {
        if (this.cancelled) return;
        this.observer = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.delay = setTimeout(() => {
            if (this.cancelled || document.hidden || !this.isConnected || track.scrollLeft > 1 ||
                matchMedia('(prefers-reduced-motion: reduce)').matches) return;
            const distance = Math.min(36, track.scrollWidth - track.clientWidth);
            if (distance < 4) return;
            track.scrollTo({ left: distance, behavior: 'smooth' });
            this.returnTimer = setTimeout(() => {
              if (!this.cancelled && this.isConnected) track.scrollTo({ left: 0, behavior: 'smooth' });
            }, 1100);
          }, 700);
        });
        this.observer.observe(this);
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true, signal: this.abort.signal });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.cancelled = true;
      clearTimeout(this.delay);
      clearTimeout(this.returnTimer);
      this.observer?.disconnect();
    }
  });
}
